import React from "react";
import { Youtube, Instagram, Users, Mail, Monitor } from "lucide-react";
import useScrollReveal from "@/hooks/useScrollReveal";
import duduAvatar from "@/assets/dudu-avatar.jpeg";
import growthLogo from "@/assets/growth-logo.png";

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-6.95a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
  </svg>
);

const Links: React.FC = () => {
  useScrollReveal();

  const socials = [
    { icon: Instagram, href: "https://www.instagram.com/duducrespo_/", label: "Instagram" },
    { icon: TikTokIcon, href: "https://www.tiktok.com/@duducrespo__", label: "TikTok" },
    { icon: Youtube, href: "https://www.youtube.com/@duducrespo_", label: "YouTube" },
    { icon: Mail, href: "https://duducrespo.substack.com/", label: "Newsletter" },
  ];

  type LinkItem = {
    title: string;
    subtitle: string;
    href: string;
    isComingSoon?: boolean;
    icon?: React.ComponentType<{ className?: string }>;
    image?: string;
    iconBg?: string;
    iconColor?: string;
    imageBg?: string;
  };

  const linkItems: LinkItem[] = [
    {
      title: "ESCAPE CLUB | Lista de espera",
      subtitle: "Comunidade, Desafio e Aplicativo",
      icon: Users,
      href: "https://chat.whatsapp.com/G9DDj4JIOouHgXSnWlnCxd",
      isComingSoon: true,
      iconBg: "bg-white",
      iconColor: "text-black",
    },
    {
      title: "Growth 🏷️ DUDUCRESPO",
      subtitle: "gsuplementos.com.br",
      image: growthLogo,
      imageBg: "bg-black",
      href: "https://www.gsuplementos.com.br/?cupom=duducrespo",
    },
    {
      title: "Newsletter – Substack",
      subtitle: "Insights semanais e reflexões",
      icon: Mail,
      href: "https://duducrespo.substack.com/",
      iconBg: "bg-orange-500",
      iconColor: "text-white",
    },
    {
      title: "Canal do YouTube",
      subtitle: "Conteúdo exclusivo e insights",
      icon: Youtube,
      href: "https://www.youtube.com/@duducrespo_",
      iconBg: "bg-red-600",
      iconColor: "text-white",
    },
    {
      title: "Instagram",
      subtitle: "Acompanhe o dia a dia",
      icon: Instagram,
      href: "https://www.instagram.com/duducrespo_/",
      iconBg: "bg-purple-600",
      iconColor: "text-white",
    },
    {
      title: "TikTok",
      subtitle: "Conteúdo rápido e direto",
      icon: TikTokIcon,
      href: "https://www.tiktok.com/@duducrespo__",
      iconBg: "bg-black",
      iconColor: "text-white",
    },
    {
      title: "ESC Mídias – Agência Digital",
      subtitle: "Transforme sua presença digital",
      icon: Monitor,
      href: "https://escmidias.site/",
      iconBg: "bg-neutral-100",
      iconColor: "text-black",
    },
    {
      title: "Contato",
      subtitle: "contatoduducrespo@gmail.com",
      icon: Mail,
      href: "mailto:contatoduducrespo@gmail.com",
      iconBg: "bg-neutral-100",
      iconColor: "text-black",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-black font-sans">
      <section className="pt-16 pb-8 px-6 max-w-xl mx-auto text-center">
        <div className="scroll-reveal">
          <div className="w-28 h-28 mx-auto rounded-full overflow-hidden border border-black/10 shadow-sm mb-5 bg-neutral-100">
            <img
              src={duduAvatar}
              alt="Dudu Crespo"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            DUDU CRESPO
          </h1>
          <p className="text-sm md:text-base text-neutral-600 mt-2">
            Escape do óbvio. Links, cupons e conteúdo.
          </p>
          <div className="flex justify-center gap-5 mt-5">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="text-black hover:opacity-70 transition-opacity"
              >
                <s.icon className="w-6 h-6" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 max-w-xl mx-auto pb-20">
        <div className="space-y-3">
          {linkItems.map((item, index) => {
            const baseClass =
              "flex items-center gap-4 w-full p-4 rounded-xl border border-black/10 bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200";
            const Icon = item.icon;
            const content = (
              <>
                {item.image ? (
                  <div className={`shrink-0 w-11 h-11 rounded-lg overflow-hidden flex items-center justify-center ${item.imageBg ?? "bg-neutral-100"}`}>
                    <img src={item.image} alt="" className="w-full h-full object-contain p-1" />
                  </div>
                ) : Icon ? (
                  <div className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${item.iconBg ?? "bg-neutral-100"}`}>
                    <Icon className={`w-5 h-5 ${item.iconColor ?? "text-black"}`} />
                  </div>
                ) : null}
                <div className="flex-1 text-center min-w-0">
                  <h3 className="text-base font-semibold truncate">{item.title}</h3>
                  <p className="text-xs truncate text-neutral-600">{item.subtitle}</p>
                </div>
              </>
            );

            return (
              <div
                key={item.title}
                className="scroll-reveal"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {item.isComingSoon && (
                  <div className="mb-2 text-center">
                    <span className="inline-block rounded-full bg-black px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white">
                      app em pré-venda · menor preço até dia 9
                    </span>
                    <p className="mt-1.5 text-[10px] uppercase tracking-wider text-neutral-400">
                      grupo 1 lotado · grupo 2 lotado · grupo 3 aberto
                    </p>
                  </div>
                )}
                {item.isComingSoon ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative block rounded-xl p-[1.5px] overflow-hidden hover:-translate-y-0.5 transition-transform duration-200"
                  >
                    <span
                      aria-hidden
                      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] aspect-square animate-[spin_3s_linear_infinite] bg-[conic-gradient(from_0deg,transparent_0deg,transparent_280deg,#000_360deg)]"
                    />
                    <div className="relative flex items-center gap-4 w-full p-4 rounded-[10px] bg-white/50 backdrop-blur-md text-black">
                      {content}
                    </div>
                  </a>
                ) : (
                  <a
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : "_self"}
                    rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className={baseClass}
                  >
                    {content}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <footer className="py-8 text-center text-xs text-neutral-500 border-t border-black/10">
        <p>© {new Date().getFullYear()} DUDU CRESPO. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
};

export default Links;
